import React from "react";
import { Label } from "ui/components/label";
import CalendarDatePicker from "ui/components/comp-507";
import UserCombobox from "components/directus/combobox/user";
import CompanyCombobox from "components/directus/company-combobox";
import ProjectCombobox from "components/directus/combobox/project";
import { RadioGroup, RadioGroupItem } from "ui/components/radio-group";
import { JOB_TYPES } from "@/lib/constants";
import { Button } from "ui/components/button";
import { EMPLOYEE_FILTERS } from "components/employee/constants";

export function SalesCoordinatorFilter({
  label,
  localFilters,
  handleSetLocalFilters,
}) {
  return (
    <UserCombobox
      label="Sales Coordinator"
      value={localFilters?.contacts?.projects?.sales_coordinator?._eq}
      onChange={(selectedItem) => {
        handleSetLocalFilters({
          contacts: {
            ...localFilters.contacts,
            projects: {
              ...localFilters?.contacts?.projects,
              sales_coordinator: { _eq: selectedItem },
            },
          },
          deep: {
            ...(localFilters?.deep || {}),
            contacts: {
              ...(localFilters?.deep?.contacts || {}),
              _filter: {
                ...(localFilters?.deep?.contacts?._filter || {}),
                projects: {
                  ...(localFilters?.deep?.contacts?._filter?.projects || {}),
                  sales_coordinator: { _eq: selectedItem },
                },
              },
              projects: {
                ...(localFilters?.deep?.contacts?.projects || {}),
                _filter: {
                  ...(localFilters?.deep?.contacts?.projects?._filter || {}),
                  sales_coordinator: { _eq: selectedItem },
                },
              },
            },
          },
        });
      }}
      filter={EMPLOYEE_FILTERS.sales_coordinating}
      placeholder="Select"
      searchPlaceholder="Search coordinators..."
    />
  );
}

export function AccountManagerFilter({
  label,
  localFilters,
  handleSetLocalFilters,
}) {
  return (
    <UserCombobox
      label="Account Manager"
      value={localFilters?.contacts?.projects?.account_manager?._eq}
      onChange={(selectedItem) => {
        handleSetLocalFilters({
          contacts: {
            ...localFilters.contacts,
            projects: {
              ...localFilters?.contacts?.projects,
              account_manager: { _eq: selectedItem },
            },
          },
          deep: {
            ...(localFilters?.deep || {}),
            contacts: {
              ...(localFilters?.deep?.contacts || {}),
              _filter: {
                ...(localFilters?.deep?.contacts?._filter || {}),
                projects: {
                  ...(localFilters?.deep?.contacts?._filter?.projects || {}),
                  account_manager: { _eq: selectedItem },
                },
              },
              projects: {
                ...(localFilters?.deep?.contacts?.projects || {}),
                _filter: {
                  ...(localFilters?.deep?.contacts?.projects?._filter || {}),
                  account_manager: { _eq: selectedItem },
                },
              },
            },
          },
        });
      }}
      filter={EMPLOYEE_FILTERS.account_manager}
      placeholder="Select"
      searchPlaceholder="Search managers..."
    />
  );
}

export function ProjectFilter({ localFilters, handleSetLocalFilters }) {
  return (
    <ProjectCombobox
      value={localFilters?.contacts?.projects?.id?._eq}
      onChange={(selectedItem) =>
        handleSetLocalFilters({
          contacts: {
            ...localFilters.contacts,
            projects: {
              ...localFilters?.contacts?.projects,
              id: { _eq: selectedItem },
            },
          },
          deep: {
            ...(localFilters?.deep || {}),
            contacts: {
              ...(localFilters?.deep?.contacts || {}),
              _filter: {
                ...(localFilters?.deep?.contacts?._filter || {}),
                projects: {
                  ...(localFilters?.deep?.contacts?._filter?.projects || {}),
                  id: { _eq: selectedItem },
                },
              },
              projects: {
                ...(localFilters?.deep?.contacts?.projects || {}),
                _filter: {
                  ...(localFilters?.deep?.contacts?.projects?._filter || {}),
                  id: { _eq: selectedItem },
                },
              },
            },
          },
        })
      }
      placeholder="Select"
      searchPlaceholder="Search projects..."
    />
  );
}

export function TypeFilter({ localFilters, handleSetLocalFilters }) {
  return (
    <div>
      <Label className="mb-2 text-sm">Type</Label>
      <RadioGroup
        value={localFilters?.contacts?.projects?.jobs?.type?._eq || ""}
        onValueChange={(value) =>
          handleSetLocalFilters({
            contacts: {
              ...localFilters.contacts,
              projects: {
                ...localFilters?.contacts?.projects,
                jobs: {
                  ...localFilters?.contacts?.projects?.jobs,
                  type: value ? { _eq: value } : undefined,
                },
              },
            },
            deep: {
              ...(localFilters?.deep || {}),
              contacts: {
                ...(localFilters?.deep?.contacts || {}),
                _filter: {
                  ...(localFilters?.deep?.contacts?._filter || {}),
                  projects: {
                    ...(localFilters?.deep?.contacts?._filter?.projects || {}),
                    jobs: {
                      ...(localFilters?.deep?.contacts?._filter?.projects
                        ?.jobs || {}),
                      type: value ? { _eq: value } : undefined,
                    },
                  },
                },
                projects: {
                  ...(localFilters?.deep?.contacts?.projects || {}),
                  _filter: {
                    ...(localFilters?.deep?.contacts?.projects?._filter || {}),
                    jobs: {
                      ...(localFilters?.deep?.contacts?.projects?._filter
                        ?.jobs || {}),
                      type: value ? { _eq: value } : undefined,
                    },
                  },
                  jobs: {
                    ...(localFilters?.deep?.contacts?.projects?.jobs || {}),
                    _filter: {
                      ...(localFilters?.deep?.contacts?.projects?.jobs
                        ?._filter || {}),
                      type: value ? { _eq: value } : undefined,
                    },
                  },
                },
              },
            },
          })
        }
        className="grid grid-cols-2 gap-2"
      >
        {Object.entries(JOB_TYPES).map(([key, type]) => {
          return (
            <div key={key} className="flex items-center gap-2">
              <RadioGroupItem
                value={key}
                id={`type-${key}`}
                className="text-base"
              />
              <label htmlFor={`type-${key}`} className="text-sm cursor-pointer">
                {type.label}
              </label>
            </div>
          );
        })}
      </RadioGroup>
    </div>
  );
}

export function CompanyFilter({ localFilters, handleSetLocalFilters }) {
  return (
    <CompanyCombobox
      value={localFilters?.company?.id?._eq}
      onChange={(selectedItem) =>
        handleSetLocalFilters({
          company: { id: { _eq: selectedItem } },
        })
      }
      placeholder="Select"
      searchPlaceholder="Search companies..."
    />
  );
}

export function DateFilter({
  fieldName,
  label,
  localFilters,
  handleSetLocalFilters,
}) {
  return (
    <div>
      <Label className="mb-2 text-sm">{label}</Label>
      <CalendarDatePicker
        stack={true}
        date={{
          from: localFilters?.contacts?.projects?.jobs?.billables?.[fieldName]
            ?._gte,
          to: localFilters?.contacts?.projects?.jobs?.billables?.[fieldName]
            ?._lte,
        }}
        setDate={(range) => {
          if (!range || (!range.from && !range.to)) {
            handleSetLocalFilters({
              contacts: {
                ...localFilters.contacts,
                projects: {
                  ...localFilters?.contacts?.projects,
                  jobs: {
                    ...localFilters?.contacts?.projects?.jobs,
                    billables: {
                      ...localFilters?.contacts?.projects?.jobs?.billables,
                      [fieldName]: undefined,
                    },
                  },
                },
              },
              deep: {
                ...(localFilters?.deep || {}),
                contacts: {
                  ...(localFilters?.deep?.contacts || {}),
                  _filter: {
                    ...(localFilters?.deep?.contacts?._filter || {}),
                    projects: {
                      ...(localFilters?.deep?.contacts?._filter?.projects ||
                        {}),
                      jobs: {
                        ...(localFilters?.deep?.contacts?._filter?.projects
                          ?.jobs || {}),
                        billables: {
                          ...(localFilters?.deep?.contacts?._filter?.projects
                            ?.jobs?.billables || {}),
                          [fieldName]: undefined,
                        },
                      },
                    },
                  },
                  projects: {
                    ...(localFilters?.deep?.contacts?.projects || {}),
                    _filter: {
                      ...(localFilters?.deep?.contacts?.projects?._filter ||
                        {}),
                      jobs: {
                        ...(localFilters?.deep?.contacts?.projects?._filter
                          ?.jobs || {}),
                        billables: {
                          ...(localFilters?.deep?.contacts?.projects?._filter
                            ?.jobs?.billables || {}),
                          [fieldName]: undefined,
                        },
                      },
                    },
                    jobs: {
                      ...(localFilters?.deep?.contacts?.projects?.jobs || {}),
                      _filter: {
                        ...(localFilters?.deep?.contacts?.projects?.jobs
                          ?._filter || {}),
                        billables: {
                          ...(localFilters?.deep?.contacts?.projects?.jobs
                            ?._filter?.billables || {}),
                          [fieldName]: undefined,
                        },
                      },
                      billables: {
                        ...(localFilters?.deep?.contacts?.projects?.jobs
                          ?.billables || {}),
                        _filter: {
                          ...(localFilters?.deep?.contacts?.projects?.jobs
                            ?.billables?._filter || {}),
                          [fieldName]: undefined,
                        },
                      },
                    },
                  },
                },
              },
            });
          } else {
            handleSetLocalFilters({
              contacts: {
                ...localFilters.contacts,
                projects: {
                  ...localFilters?.contacts?.projects,
                  jobs: {
                    ...localFilters?.contacts?.projects?.jobs,
                    billables: {
                      ...localFilters?.contacts?.projects?.jobs?.billables,
                      [fieldName]: {
                        _gte: range?.from,
                        _lte: range?.to,
                      },
                    },
                  },
                },
              },
              deep: {
                ...(localFilters?.deep || {}),
                contacts: {
                  ...(localFilters?.deep?.contacts || {}),
                  _filter: {
                    ...(localFilters?.deep?.contacts?._filter || {}),
                    projects: {
                      ...(localFilters?.deep?.contacts?._filter?.projects ||
                        {}),
                      jobs: {
                        ...(localFilters?.deep?.contacts?._filter?.projects
                          ?.jobs || {}),
                        billables: {
                          ...(localFilters?.deep?.contacts?._filter?.projects
                            ?.jobs?.billables || {}),
                          [fieldName]: {
                            _gte: range?.from,
                            _lte: range?.to,
                          },
                        },
                      },
                    },
                  },
                  projects: {
                    ...(localFilters?.deep?.contacts?.projects || {}),
                    _filter: {
                      ...(localFilters?.deep?.contacts?.projects?._filter ||
                        {}),
                      jobs: {
                        ...(localFilters?.deep?.contacts?.projects?._filter
                          ?.jobs || {}),
                        billables: {
                          ...(localFilters?.deep?.contacts?.projects?._filter
                            ?.jobs?.billables || {}),
                          [fieldName]: {
                            _gte: range?.from,
                            _lte: range?.to,
                          },
                        },
                      },
                    },
                    jobs: {
                      ...(localFilters?.deep?.contacts?.projects?.jobs || {}),
                      _filter: {
                        ...(localFilters?.deep?.contacts?.projects?.jobs
                          ?._filter || {}),
                        billables: {
                          ...(localFilters?.deep?.contacts?.projects?.jobs
                            ?._filter?.billables || {}),
                          [fieldName]: {
                            _gte: range?.from,
                            _lte: range?.to,
                          },
                        },
                      },
                      billables: {
                        ...(localFilters?.deep?.contacts?.projects?.jobs
                          ?.billables || {}),
                        _filter: {
                          ...(localFilters?.deep?.contacts?.projects?.jobs
                            ?.billables?._filter || {}),
                          [fieldName]: {
                            _gte: range?.from,
                            _lte: range?.to,
                          },
                        },
                      },
                    },
                  },
                },
              },
            });
          }
        }}
        className="w-full"
      />
    </div>
  );
}

export function FilterActions({ applyFilters, clearLocalFilters }) {
  return (
    <div className="flex place-self-end gap-4">
      <Button variant="outline" onClick={clearLocalFilters}>
        Clear
      </Button>
      <Button onClick={applyFilters}>Apply</Button>
    </div>
  );
}
